import {type FormEvent, forwardRef, useCallback, useImperativeHandle, useState} from 'react'
import {Button, DialogActions, FormHelperText, Stack, TextField} from '@mui/material'
import {Trans, useTranslation} from 'react-i18next'
import {signInWithEmailAndPassword} from 'firebase/auth'
import {FirebaseError} from 'firebase/app'

import {useFirebaseAuth} from '@goschool/react-firebase'
import {NoWrapButton, NoWrapFormHelperText} from '@goschool/mui'
import {useAuthenticationContext} from './AuthenticationContext'
import {useSignInContext} from './SignInContext'

export interface CredentialsSignInHandle {
  /** Imperatively submit the credentials sign-in flow */
  submit: (e: FormEvent) => Promise<void>
  /** Reset input fields and validation errors */
  reset: () => void
}

export const CredentialsSignIn = forwardRef<CredentialsSignInHandle>(
  function CredentialsSignIn(props, ref) {
    const {t} = useTranslation()
    const {auth} = useFirebaseAuth()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')

    const [emailError, setEmailError] = useState<string | null>(null)
    const [passwordError, setPasswordError] = useState<string | null>(null)
    const {state, setState, handleError, handleSuccess, resetError} = useSignInContext()


    const resetFields = useCallback(() => {
      setEmail('')
      setPassword('')
    }, [])

    const resetErrors = useCallback(() => {
      setEmailError(null)
      setPasswordError(null)
      resetError()
    }, [resetError])

    const reset = useCallback(() => {
      resetFields()
      resetErrors()
    }, [resetErrors, resetFields])


    const {enabledMethods} = useAuthenticationContext()

    const doSubmit = useCallback(
      async (e: FormEvent) => {
        e.preventDefault()
        if (!['idle', 'failed'].includes(state)) {
          throw new Error(`Cannot submit while in state ${state}`)
        }

        setState('pending')
        resetErrors()
        try {
          const credential = await signInWithEmailAndPassword(auth, email, password)
          await handleSuccess(credential)
          resetFields()
          setState('idle')
        } catch (e) {
          if (e instanceof FirebaseError) {
            if (e.code === 'auth/invalid-email') {
              setEmailError('Invalid email')
            } else if (e.code === 'auth/missing-password') {
              setPasswordError('Missing password')
            } else {
              handleError(e, 'credentials')
            }
          } else {
            handleError(e, 'credentials')
          }
        }
      }, [auth, email, handleError, handleSuccess, password, resetErrors, resetFields, setState, state])

    useImperativeHandle(ref, () => ({
      submit: doSubmit,
      reset: reset
    }), [doSubmit, reset])

    if (!enabledMethods.includes('credentials')) {
      return null
    }

    return <>
      <FormHelperText><Trans i18nKey="auth:signIn.form.useCredentials"/></FormHelperText>
      <TextField
        size="small" fullWidth={true}
        label={t('auth:signIn.form.email')} value={email}
        error={emailError != null} helperText={emailError}
        onChange={e => setEmail(e.target.value)}/>

      <TextField
        size="small" fullWidth={true} type="password"
        error={passwordError != null} helperText={passwordError}
        label={t('auth:signIn.form.password')} value={password}
        onChange={e => setPassword(e.target.value)}/>
    </>
  }
)


export function CredentialSignInHelperActions() {
  const {enabledMethods, resetPassword} = useAuthenticationContext()

  if (!enabledMethods.includes('credentials')) {
    return null
  }

  return <Stack direction="row" justifyContent="flex-end" alignItems="baseline">
    <NoWrapFormHelperText><Trans i18nKey="auth:signIn.form.forgot"/></NoWrapFormHelperText>
    <NoWrapButton variant="text" sx={{textTransform: 'inherit'}} onClick={resetPassword} size="small"><Trans
      i18nKey="auth:signIn.form.reset"/></NoWrapButton>
  </Stack>
}


export function CredentialsSignInActions() {
  const {state} = useSignInContext()
  const {enabledMethods, signUp} = useAuthenticationContext()

  if (!enabledMethods.includes('credentials')) {
    return null
  }

  return <DialogActions>
    <Stack direction="row" justifyContent="flex-start" alignItems="baseline" flexWrap="wrap">
      <NoWrapFormHelperText><Trans i18nKey="auth:signIn.form.newHere"/></NoWrapFormHelperText>
      <NoWrapButton variant="text" sx={{textTransform: 'inherit'}} onClick={signUp} size="small"><Trans
        i18nKey="auth:signIn.form.signUp"/></NoWrapButton>
    </Stack>
    <Button sx={{flexShrink: 0}} type="submit" variant="contained" color="primary"
      loading={state === 'pending'} autoFocus={true}>
      <Trans i18nKey="auth:signIn.form.submit"/>
    </Button>
  </DialogActions>
}
